/**
 * Verbesserungsvorschlag – Detailansicht.
 *
 * Zweispaltig wie die Angebotsmaske: links „Worum es geht" und der Verlauf,
 * rechts die Eckdaten und der Entscheidungsblock.
 *
 * Der Verlauf ist eine echte Historie, kein einzelnes Antwortfeld. So sieht man
 * auch Rückfragen und Zwischenschritte – und erkennt, dass ein Vorschlag nicht
 * liegen geblieben ist. Der aktuelle Status ist immer der letzte Eintrag, der
 * einer ist; ein zweites Statusfeld gibt es nicht, es würde auseinanderlaufen.
 */

import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { EntscheidungModal } from '../modals/EntscheidungModal';
import { StatusPille, datKurz } from './Vorschlaege';
import type { Vorschlag } from '../typen';
import './vorschlaege.css';

/** Was der Administrator aus dem jeweiligen Zustand heraus tun kann. */
const SCHRITTE: Record<string, string[]> = {
  Eingebracht: ['Rückfrage', 'Genehmigt', 'Abgelehnt'],
  Genehmigt: ['Rückfrage', 'Umgesetzt', 'Abgelehnt'],
  Umgesetzt: ['Rückfrage'],
  Abgelehnt: ['Rückfrage', 'Genehmigt'],
};

export function VorschlagDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ort = useLocation();
  const { nutzer } = useKora();
  const [schritt, setSchritt] = useState<string | null>(null);

  const zurueck = (ort.state as { zurueck?: string } | null)?.zurueck ?? '/vorschlaege';

  const abfrage = useQuery({
    queryKey: ['vorschlag', id],
    queryFn: () => api.hole<Vorschlag>(`/vorschlaege/${id}`),
    enabled: !!id,
  });

  const v = abfrage.data;

  if (abfrage.isLoading) {
    return (
      <div className="eh-inhalt vv">
        <div className="vv-leer">Wird geladen …</div>
      </div>
    );
  }

  if (!v) {
    return (
      <div className="eh-inhalt vv">
        <button type="button" className="detail-zurueck" onClick={() => navigate(zurueck)}>
          ← Zurück
        </button>
        <div className="eh-karte">
          <div className="eh-titel" style={{ fontSize: 22 }}>Diesen Vorschlag gibt es nicht.</div>
        </div>
      </div>
    );
  }

  const moeglich = nutzer?.istAdmin ? (SCHRITTE[v.status] ?? []) : [];

  /*
   * Offene Rückfrage: Der letzte Eintrag ist eine Frage, und sie gilt dem, der
   * den Vorschlag eingebracht hat. Nur dann gibt es hier ein Antwortfeld – eine
   * Frage, die niemand beantworten kann, wäre keine.
   */
  const letzter = v.verlauf[v.verlauf.length - 1];
  const offeneFrage = letzter?.typ === 'Rückfrage' ? letzter : null;
  const darfAntworten = !!offeneFrage && v.ersteller === nutzer?.anzeigename;

  return (
    <div className="eh-inhalt vv">
      <button type="button" className="detail-zurueck" onClick={() => navigate(zurueck)}>
        ← Zurück
      </button>

      <div className="vv-detailkopf">
        <span className={`vv-punkt vv-punkt--${v.prioritaet}`} title={`Priorität ${v.prioritaet}`} />
        <span className="vv-kennung">{v.kennung}</span>
        <h1 className="vv-titel vv-titel--detail">{v.titel}</h1>
        <StatusPille status={v.status} />
      </div>

      <div className="vv-spalten">
        <div className="vv-links">
          <div className="eh-karte vv-block">
            <div className="vv-blocktitel">Worum es geht</div>
            <p className="vv-beschreibung">{v.beschreibung}</p>
          </div>

          <div className="eh-karte vv-block">
            <div className="vv-blocktitel">Verlauf</div>
            <div className="vv-verlauf">
              {v.verlauf.map((e) => (
                <div key={e.id} className="vv-schritt">
                  <div className={`vv-schritt-marke vv-schritt-marke--${e.typ.toLowerCase().replace('ü', 'ue')}`} />
                  <div className="vv-schritt-inhalt">
                    <div className="vv-schritt-kopf">
                      <StatusPille status={e.typ} />
                      <span className="vv-schritt-person">{e.person}</span>
                      <span className="vv-schritt-datum">{datKurz(e.angelegtAm)}</span>
                    </div>
                    {e.text && <p className="vv-schritt-text">{e.text}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="vv-rechts">
          <div className="eh-karte vv-block">
            <div className="vv-blocktitel">Eckdaten</div>
            <dl className="vv-eckdaten">
              <dt>Bereich</dt>
              <dd>{v.bereichLabel}</dd>
              <dt>Priorität</dt>
              <dd className="vv-prio">
                <span className={`vv-punkt vv-punkt--${v.prioritaet}`} />
                {v.prioritaet}
              </dd>
              <dt>Ersteller</dt>
              <dd>{v.ersteller}</dd>
              <dt>Eingebracht am</dt>
              <dd>{new Date(v.angelegtAm).toLocaleDateString('de-AT')}</dd>
            </dl>
          </div>

          <div className="eh-karte vv-block">
            <div className="vv-blocktitel">Entscheidung</div>
            {nutzer?.istAdmin ? (
              <>
                <p className="eh-hinweis vv-entscheidungshinweis">
                  {v.ersteller === nutzer?.anzeigename
                    ? 'Jede Antwort bleibt im Verlauf stehen.'
                    : `Jede Antwort geht als Mail an ${v.ersteller} und bleibt im Verlauf stehen.`}
                </p>
                <div className="vv-knoepfe">
                  {moeglich.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`vv-schrittknopf vv-schrittknopf--${s.toLowerCase().replace('ü', 'ue')}`}
                      onClick={() => setSchritt(s)}
                    >
                      {s === 'Rückfrage' ? 'Rückfrage stellen' : s}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="eh-hinweis">
                Über Vorschläge entscheidet die Geschäftsführung. Du bekommst eine Mail, sobald es
                eine Antwort gibt.
              </p>
            )}

            {darfAntworten && (
              <div className="vv-antwortblock">
                <p className="eh-hinweis vv-entscheidungshinweis">
                  {offeneFrage!.person} hat eine Rückfrage gestellt. Sie wartet auf dich.
                </p>
                <button
                  type="button"
                  className="vv-schrittknopf vv-schrittknopf--antwort"
                  onClick={() => setSchritt('Antwort')}
                >
                  Antworten
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {schritt && (
        <EntscheidungModal
          vorschlag={v}
          typ={schritt}
          fragesteller={offeneFrage?.person}
          onSchliessen={() => setSchritt(null)}
        />
      )}
    </div>
  );
}